import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getApiUrl, addUserParam } from '../config/api.config';
import { marketSession } from '../utils/session';

// NOTE: Do NOT call getApiUrl() at module load time!
// It must be called at request time when window.location is available.

// Fetch live prices for tickers
export const fetchLivePrices = createAsyncThunk(
  'tickerTape/fetchLivePrices',
  async (tickers, { rejectWithValue }) => {
    try {
      const response = await fetch(
        addUserParam(`${getApiUrl()}/prices/live?tickers=${tickers.join(',')}`),
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch prices');
      }

      const data = await response.json();
      return data.prices;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch user's ticker tape preferences
export const fetchTickerTapePreferences = createAsyncThunk(
  'tickerTape/fetchPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(
        addUserParam(`${getApiUrl()}/prices/ticker-tape`),
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch preferences');
      }

      const data = await response.json();
      return data.tickers;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Save user's ticker tape preferences
export const saveTickerTapePreferences = createAsyncThunk(
  'tickerTape/savePreferences',
  async (tickers, { rejectWithValue }) => {
    try {
      const response = await fetch(
        addUserParam(`${getApiUrl()}/prices/ticker-tape`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ tickers })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      return tickers;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// What the feed gave us, stamped with when (browser clock) and in which session
// it arrived. The Dashboard needs the time to tell a streamed price from one
// that a newer matrix payload has replaced, and the session to say what the
// price is (last / prev close / close).
const stamp = (prices, rcv = Date.now()) => {
  const rs = marketSession(rcv);
  const out = {};
  for (const ticker of Object.keys(prices)) {
    out[ticker] = prices[ticker] ? { ...prices[ticker], rcv, rs } : prices[ticker];
  }
  return out;
};

const tickerTapeSlice = createSlice({
  name: 'tickerTape',
  initialState: {
    tickers: ['TLT', 'SPY', 'QQQ', 'PFF', 'PGX'],
    prices: {},
    loading: false,
    error: null,
    lastUpdate: null
  },
  reducers: {
    addTicker: (state, action) => {
      const ticker = action.payload.toUpperCase();
      if (!state.tickers.includes(ticker)) {
        state.tickers.push(ticker);
      }
    },
    removeTicker: (state, action) => {
      const ticker = action.payload.toUpperCase();
      state.tickers = state.tickers.filter(t => t !== ticker);
      delete state.prices[ticker];
    },
    updatePrices: {
      // `receivedAt` is when the frame arrived; the socket batches frames for
      // a moment before dispatching, so it passes the time of the first one.
      prepare: (prices, receivedAt = Date.now()) => ({ payload: prices, meta: { receivedAt } }),
      reducer: (state, action) => {
        // Use Object.assign for better performance (avoids creating new object)
        Object.assign(state.prices, stamp(action.payload, action.meta.receivedAt));
        state.lastUpdate = new Date().toISOString();
      },
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch live prices
      .addCase(fetchLivePrices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLivePrices.fulfilled, (state, action) => {
        state.loading = false;
        Object.assign(state.prices, stamp(action.payload));
        state.lastUpdate = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchLivePrices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch preferences
      .addCase(fetchTickerTapePreferences.fulfilled, (state, action) => {
        state.tickers = action.payload;
      })
      // Save preferences
      .addCase(saveTickerTapePreferences.fulfilled, (state, action) => {
        state.tickers = action.payload;
      });
  }
});

export const { addTicker, removeTicker, updatePrices, clearError } = tickerTapeSlice.actions;
export default tickerTapeSlice.reducer;